import { useQuery } from '@tanstack/react-query'

import { consulta, pedir } from './nucleo'

/**
 * «Ver en la web ↗», como el enlace permanente de WordPress (CLAUDE.md §20). La dirección
 * pública de cada documento la dice el núcleo (`GET /api/panel/enlaces`), con la misma
 * regla que la web: el panel no compone ninguna. Solo la tiene lo que está publicado y se
 * ve en la web; lo demás trae por qué no, en llano.
 */
export type EnlaceEnLaWeb = { url: null | string; motivo: null | string }

export type EnlacesEnLaWeb = {
  sitio: number
  /** El dominio por el que se llega a la web: el primero de los suyos o `<slug>.wixy.app`. */
  dominio: string
  /** Si el diseño de la web está publicado: sin él, nada se ve en internet. */
  enInternet: boolean
  /** Por id. Lo que quien mira no puede leer (o no es de esta web) llega como `{ url: null, motivo: null }`. */
  enlaces: Record<string, EnlaceEnLaWeb>
}

/**
 * Las direcciones de unos documentos de una web, en **una** petición (un listado entero, no
 * una por fila). La clave cuelga de «lista»: se vuelve a pedir cuando se guarda, se publica
 * o se borra algo de esa colección, que es cuando puede cambiar.
 */
export const useEnlacesEnLaWeb = (coleccion: string, sitio: null | number, ids: number[], activo = true) =>
  useQuery({
    queryKey: ['lista', coleccion, 'enlaces', sitio, ids.join(',')],
    enabled: activo && sitio !== null && ids.length > 0,
    queryFn: () => pedir<EnlacesEnLaWeb>(`/panel/enlaces${consulta({ sitio: sitio ?? undefined, coleccion, ids: ids.join(',') })}`),
    staleTime: 30_000,
  })

export type EstadoDelEnlace =
  | { tipo: 'cargando' }
  /** Se ve en la web: la dirección, y si lo que se ve no es todavía lo último (`nota`). */
  | { tipo: 'enlace'; url: string; nota: null | string }
  /** No se ve: por qué, sin enlace. */
  | { tipo: 'sin-enlace'; texto: string }
  | { tipo: 'nada' }

export const LO_PUBLICADO = 'Lo publicado; tus cambios aún no.'

/**
 * Qué se enseña bajo el título del editor. El enlace lleva siempre a **lo publicado**: si hay
 * cambios encima —guardados como borrador, o sin guardar todavía—, se dice, para que nadie
 * abra la web esperando ver lo que acaba de escribir.
 *
 * `estado` es el `_status` del documento que se edita (la última versión): en una colección
 * con borradores, «draft» con dirección quiere decir «publicado, con cambios sin publicar».
 */
export const estadoDelEnlace = (o: { enlace: EnlaceEnLaWeb | undefined; cargando: boolean; borradores: boolean; estado: string; sucio: boolean }): EstadoDelEnlace => {
  if (o.cargando) return { tipo: 'cargando' }
  if (!o.enlace) return { tipo: 'nada' }
  if (o.enlace.url) {
    const conCambios = o.sucio || (o.borradores && o.estado !== 'published')
    return { tipo: 'enlace', url: o.enlace.url, nota: conCambios ? LO_PUBLICADO : null }
  }
  return o.enlace.motivo ? { tipo: 'sin-enlace', texto: o.enlace.motivo } : { tipo: 'nada' }
}

/** La dirección de una fila del listado, si la tiene (lo demás no lleva icono). */
export const urlDeLaFila = (datos: EnlacesEnLaWeb | undefined, id: number): null | string => datos?.enlaces[String(id)]?.url ?? null

/** La dirección corta de una fila, sin el dominio: «/propiedades/depa-x». La portada, «/». */
export const rutaCorta = (url: string): string => {
  try {
    const u = new URL(url)
    return decodeURI(u.pathname) || '/'
  } catch {
    return url
  }
}
